import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { newPackingSession, getPallet, addItem, generateBoxes, finalizePallet, getCustomers, detectCategory, getAllBoxLabels } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Package, Plus, Check, ArrowRight, Printer, Truck, Save } from 'lucide-react';
import { CATEGORY_COLORS, CATEGORY_LABELS, FINISH_OPTIONS } from '@/lib/constants';
import DraftPalletsCard from '@/components/DraftPalletsCard';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const defaultItem = {
  item_code: '', description: '', size: '', grade: '', standard: '', finish: '',
  qty_per_carton: '', full_cartons: '', has_exception: false, exception_qty: '',
  exception_net_wt: '', exception_gross_wt: '',
  net_wt_per_carton: '', gross_wt_per_carton: '',
  batch_no: '', heat_no: '', customer_id: '', category: ''
};

export default function PackPage() {
  const { palletId } = useParams();
  const navigate = useNavigate();
  const [pallet, setPallet] = useState(null);
  const [step, setStep] = useState(1);
  const [currentItem, setCurrentItem] = useState({ ...defaultItem });
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detectedCategory, setDetectedCategory] = useState('');

  useEffect(() => {
    getCustomers().then(r => setCustomers(r.data)).catch((err) => { console.error('Failed to load customers:', err); });
    if (palletId) loadPallet(palletId);
  }, [palletId]);

  const loadPallet = async (id) => {
    try {
      const res = await getPallet(id);
      setPallet(res.data);
      if (res.data.status === 'ready') setStep(3);
      if (res.data.status === 'placed') setStep(4);
    } catch (err) { toast.error('Failed to load pallet'); console.error(err); }
  };

  const handleNewSession = async () => {
    setLoading(true);
    try {
      const res = await newPackingSession();
      setPallet(res.data);
      toast.success(`Pallet ${res.data.pallet_ref} reserved`);
      navigate(`/pack/${res.data.id}`, { replace: true });
    } catch (err) { toast.error('Failed to start session'); console.error(err); }
    finally { setLoading(false); }
  };

  const handleDescriptionChange = async (val) => {
    setCurrentItem(prev => ({ ...prev, description: val }));
    if (val.length > 2) {
      try {
        const res = await detectCategory(val);
        setDetectedCategory(res.data.category);
        setCurrentItem(prev => ({ ...prev, category: res.data.category }));
      } catch (err) { /* category auto-detection optional */ }
    }
  };

  // Smart calculations
  const calc = useMemo(() => {
    const qpc = parseInt(currentItem.qty_per_carton) || 0;
    const fc = parseInt(currentItem.full_cartons) || 0;
    const hasExc = currentItem.has_exception;
    const excQty = parseInt(currentItem.exception_qty) || 0;
    const netPerCtn = parseFloat(currentItem.net_wt_per_carton) || 0;
    const grossPerCtn = parseFloat(currentItem.gross_wt_per_carton) || 0;
    const excNetWt = parseFloat(currentItem.exception_net_wt) || 0;
    const excGrossWt = parseFloat(currentItem.exception_gross_wt) || 0;

    const totalCartons = fc + (hasExc && excQty > 0 ? 1 : 0);
    const totalQty = (qpc * fc) + (hasExc ? excQty : 0);
    const totalNet = (netPerCtn * fc) + (hasExc && excQty > 0 ? excNetWt : 0);
    const totalGross = (grossPerCtn * fc) + (hasExc && excQty > 0 ? excGrossWt : 0);
    const unitWeight = totalQty > 0 ? (totalNet / totalQty) : 0;
    const excError = hasExc && excQty >= qpc && qpc > 0;

    return { totalCartons, totalQty, totalNet, totalGross, unitWeight, excError };
  }, [currentItem]);

  const handleAddItem = async () => {
    if (!currentItem.item_code || !currentItem.description) return toast.error('Item code and description are required');
    if (calc.totalQty <= 0) return toast.error('Quantity must be greater than 0');
    if (calc.excError) return toast.error('Exception qty must be less than standard carton qty');
    setLoading(true);
    try {
      await addItem(pallet.id, {
        item_code: currentItem.item_code,
        description: currentItem.description,
        size: currentItem.size,
        grade: currentItem.grade,
        standard: currentItem.standard,
        finish: currentItem.finish,
        category: currentItem.category,
        qty_pcs_total: calc.totalQty,
        qty_weight_net_kg: Math.round(calc.totalNet * 100) / 100,
        qty_weight_gross_kg: Math.round(calc.totalGross * 100) / 100,
        unit_weight_kg: calc.unitWeight > 0 ? Math.round(calc.unitWeight * 100000) / 100000 : null,
        ctns_count: calc.totalCartons,
        ctns_standard_qty: parseInt(currentItem.qty_per_carton) || 0,
        ctns_standard_weight_net_kg: parseFloat(currentItem.net_wt_per_carton) || 0,
        ctns_standard_weight_gross_kg: parseFloat(currentItem.gross_wt_per_carton) || 0,
        has_exception_carton: currentItem.has_exception,
        exception_carton_qty: currentItem.has_exception ? (parseInt(currentItem.exception_qty) || 0) : 0,
        exception_carton_net_wt: currentItem.has_exception ? (parseFloat(currentItem.exception_net_wt) || 0) : 0,
        exception_carton_gross_wt: currentItem.has_exception ? (parseFloat(currentItem.exception_gross_wt) || 0) : 0,
        batch_no: currentItem.batch_no || null,
        heat_no: currentItem.heat_no || null,
        customer_id: currentItem.customer_id || null,
      });
      toast.success('Item added');
      setCurrentItem({ ...defaultItem });
      setDetectedCategory('');
      await loadPallet(pallet.id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add item'); }
    finally { setLoading(false); }
  };

  const handleGenerateBoxes = async () => {
    setLoading(true);
    try {
      await generateBoxes(pallet.id);
      toast.success('Box labels generated');
      setStep(2);
      await loadPallet(pallet.id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  const handleFinalize = async () => {
    setLoading(true);
    try {
      await finalizePallet(pallet.id);
      toast.success('Pallet finalized');
      setStep(3);
      await loadPallet(pallet.id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  if (!palletId) {
    return (
      <div className="space-y-4 max-w-2xl mx-auto" data-testid="pack-start">
        {/* Resume drafts (if any) */}
        <DraftPalletsCard onlyMine={false} compact />

        {/* New session */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6 flex flex-col items-center justify-center space-y-3 text-center">
            <Package size={40} className="text-[#1a2fce]" />
            <h2 className="text-base font-semibold">Start a New Pallet</h2>
            <p className="text-xs text-gray-500 max-w-xs">
              We’ll reserve the next pallet number (e.g. <span className="font-mono">SFX-0042</span>). You can leave at any time — the draft is saved automatically and shown on your dashboard.
            </p>
            <Button onClick={handleNewSession} disabled={loading} className="h-12 px-8 text-sm font-semibold" style={{ backgroundColor: '#1a2fce' }} data-testid="start-session-btn">
              <Plus size={16} className="mr-1.5" /> {loading ? 'Starting…' : 'Start Packing Session'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!pallet) return <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-4 border-[#1a2fce] border-t-transparent rounded-full" /></div>;

  const totalPcs = pallet.items?.reduce((s, i) => s + (i.qty_pcs_total || 0), 0) || 0;
  const totalNet = pallet.items?.reduce((s, i) => s + (i.qty_weight_net_kg || 0), 0) || 0;
  const totalCtns = pallet.items?.reduce((s, i) => s + (i.ctns_count || 0), 0) || 0;

  return (
    <div className="space-y-4 max-w-2xl mx-auto" data-testid="pack-page">
      {/* Pallet Header */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-2xl font-mono font-bold text-[#0d1f6e]">{pallet.pallet_ref}</div>
              <Badge className="mt-1 text-xs" variant="outline">{pallet.status}</Badge>
            </div>
            <div className="text-right text-sm text-gray-500">
              <div>{totalCtns} / {pallet.pallet_capacity} ctns</div>
              <div>{totalPcs.toLocaleString()} pcs · {totalNet.toFixed(1)} kg</div>
            </div>
          </div>

          {pallet.status === 'packing' && (
            <div className="mt-3 flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg" data-testid="draft-saved-banner">
              <Save size={14} className="text-amber-700 shrink-0" />
              <div className="text-[11px] text-amber-900 flex-1">
                <span className="font-semibold">Draft auto-saved.</span> You can leave anytime — this pallet stays in drafts until you finalize.
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  toast.success(`Draft ${pallet.pallet_ref} saved. Resume anytime from Dashboard.`);
                  navigate('/');
                }}
                className="h-7 text-[11px] gap-1 bg-white"
                data-testid="save-exit-btn"
              >
                <Save size={11} /> Save &amp; Exit
              </Button>
            </div>
          )}

          <div className="flex items-center gap-1 mt-4">
            {['Items', 'Labels', 'Finalize', 'Place'].map((s, i) => (
              <React.Fragment key={s}>
                <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                  step > i + 1 ? 'bg-green-100 text-green-700' :
                  step === i + 1 ? 'bg-[#1a2fce] text-white' : 'bg-gray-100 text-gray-400'
                }`}>
                  {step > i + 1 ? <Check size={12} /> : <span>{i + 1}</span>}
                  <span className="hidden sm:inline">{s}</span>
                </div>
                {i < 3 && <ArrowRight size={12} className="text-gray-300" />}
              </React.Fragment>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Step 1: Add Items */}
      {step === 1 && pallet.status === 'packing' && (
        <>
          {pallet.items?.length > 0 && (
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Items on Pallet ({pallet.items.length})</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {pallet.items.map((item) => (
                  <div key={item.item_code || item.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                    <div className="w-1 h-10 rounded-full" style={{ backgroundColor: CATEGORY_COLORS[item.category] || '#6b7280' }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{item.description}</div>
                      <div className="text-xs text-gray-500">{item.item_code} · {item.qty_pcs_total} pcs · {item.ctns_count} ctns</div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Smart Add Item Form */}
          <Card className="border-0 shadow-sm" data-testid="add-item-form">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Plus size={14} /> Add Item
                {detectedCategory && (
                  <Badge className="text-[10px] text-white ml-auto" style={{ backgroundColor: CATEGORY_COLORS[detectedCategory] }}>
                    {CATEGORY_LABELS[detectedCategory]}
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Row 1: Item Code · Category */}
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Item Code *</Label><Input value={currentItem.item_code} onChange={e => setCurrentItem(p => ({...p, item_code: e.target.value}))} className="h-9 text-sm" data-testid="item-code" /></div>
                <div><Label className="text-xs">Category</Label>
                  <Select value={currentItem.category} onValueChange={v => setCurrentItem(p => ({...p, category: v}))}>
                    <SelectTrigger className="h-9 text-xs" data-testid="item-category"><SelectValue placeholder="Auto-detect" /></SelectTrigger>
                    <SelectContent>{Object.entries(CATEGORY_LABELS).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              {/* Row 2: Description */}
              <div><Label className="text-xs">Description *</Label><Input value={currentItem.description} onChange={e => handleDescriptionChange(e.target.value)} className="h-9 text-sm" data-testid="item-description" /></div>

              {/* Row 3: Size · Grade · Standard · Finish */}
              <div className="grid grid-cols-4 gap-2">
                <div><Label className="text-xs">Size</Label><Input value={currentItem.size} onChange={e => setCurrentItem(p => ({...p, size: e.target.value}))} className="h-9 text-sm" placeholder="M10×40" /></div>
                <div><Label className="text-xs">Grade</Label><Input value={currentItem.grade} onChange={e => setCurrentItem(p => ({...p, grade: e.target.value}))} className="h-9 text-sm" placeholder="8.8" /></div>
                <div><Label className="text-xs">Standard</Label><Input value={currentItem.standard} onChange={e => setCurrentItem(p => ({...p, standard: e.target.value}))} className="h-9 text-sm" placeholder="DIN985" /></div>
                <div><Label className="text-xs">Finish</Label>
                  <Select value={currentItem.finish} onValueChange={v => setCurrentItem(p => ({...p, finish: v}))}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{FINISH_OPTIONS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              <Separator />

              {/* Row 4: Qty per Carton · No. of Full Cartons */}
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Qty per Carton (pcs) *</Label><Input type="number" value={currentItem.qty_per_carton} onChange={e => setCurrentItem(p => ({...p, qty_per_carton: e.target.value}))} className="h-9 text-sm" data-testid="qty-per-carton" placeholder="50" /></div>
                <div><Label className="text-xs">No. of Full Cartons *</Label><Input type="number" value={currentItem.full_cartons} onChange={e => setCurrentItem(p => ({...p, full_cartons: e.target.value}))} className="h-9 text-sm" data-testid="full-cartons" placeholder="30" /></div>
              </div>

              {/* Row 5: Exception Carton */}
              <div className="flex items-center gap-2">
                <Checkbox checked={currentItem.has_exception} onCheckedChange={checked => setCurrentItem(p => ({...p, has_exception: checked}))} id="exception" data-testid="exception-check" />
                <Label htmlFor="exception" className="text-xs text-gray-700">One non-standard carton with different qty</Label>
              </div>

              {/* Row 6: Exception Qty + Weights (visible only if checked) */}
              {currentItem.has_exception && (
                <div className="space-y-2 p-3 border border-amber-200 bg-amber-50 rounded-lg">
                  <div className="text-xs font-semibold text-amber-800 mb-1">Exception Carton Details</div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-xs">Qty (pcs)</Label>
                      <Input type="number" value={currentItem.exception_qty} onChange={e => setCurrentItem(p => ({...p, exception_qty: e.target.value}))} className={`h-9 text-sm ${calc.excError ? 'border-red-500' : ''}`} data-testid="exception-qty" placeholder="Less than std" />
                    </div>
                    <div>
                      <Label className="text-xs">Net Wt (kg)</Label>
                      <Input type="number" step="0.01" value={currentItem.exception_net_wt} onChange={e => setCurrentItem(p => ({...p, exception_net_wt: e.target.value}))} className="h-9 text-sm" data-testid="exception-net-wt" placeholder="0.00" />
                    </div>
                    <div>
                      <Label className="text-xs">Gross Wt (kg)</Label>
                      <Input type="number" step="0.01" value={currentItem.exception_gross_wt} onChange={e => setCurrentItem(p => ({...p, exception_gross_wt: e.target.value}))} className="h-9 text-sm" data-testid="exception-gross-wt" placeholder="0.00" />
                    </div>
                  </div>
                  {calc.excError && <p className="text-xs text-red-500">Exception qty must be less than standard carton qty.</p>}
                </div>
              )}

              {/* Row 7: Net/Gross Weight per Carton */}
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Net Wt per Carton (kg) *</Label><Input type="number" step="0.01" value={currentItem.net_wt_per_carton} onChange={e => setCurrentItem(p => ({...p, net_wt_per_carton: e.target.value}))} className="h-9 text-sm" data-testid="net-per-carton" placeholder="1.25" /></div>
                <div><Label className="text-xs">Gross Wt per Carton (kg) *</Label><Input type="number" step="0.01" value={currentItem.gross_wt_per_carton} onChange={e => setCurrentItem(p => ({...p, gross_wt_per_carton: e.target.value}))} className="h-9 text-sm" data-testid="gross-per-carton" placeholder="1.30" /></div>
              </div>

              {/* Row 8: Auto-calculated summary */}
              {(calc.totalQty > 0 || calc.totalCartons > 0) && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="calc-summary">
                  <div className="text-xs font-semibold text-blue-800 mb-1">Auto-Calculated Totals</div>
                  <div className="grid grid-cols-5 gap-2 text-center">
                    <div><div className="text-lg font-bold text-blue-900">{calc.totalCartons}</div><div className="text-[10px] text-blue-600">Cartons</div></div>
                    <div><div className="text-lg font-bold text-blue-900">{calc.totalQty.toLocaleString()}</div><div className="text-[10px] text-blue-600">Total Qty</div></div>
                    <div><div className="text-lg font-bold text-blue-900">{calc.totalNet.toFixed(2)}</div><div className="text-[10px] text-blue-600">Net Wt (kg)</div></div>
                    <div><div className="text-lg font-bold text-blue-900">{calc.totalGross.toFixed(2)}</div><div className="text-[10px] text-blue-600">Gross (kg)</div></div>
                    <div><div className="text-lg font-bold text-blue-900">{calc.unitWeight.toFixed(5)}</div><div className="text-[10px] text-blue-600">kg/pc</div></div>
                  </div>
                </div>
              )}

              {/* Row 9: Batch / Heat */}
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Batch No (optional)</Label><Input value={currentItem.batch_no} onChange={e => setCurrentItem(p => ({...p, batch_no: e.target.value}))} className="h-9 text-sm" placeholder="B-2026-03-001" /></div>
                <div><Label className="text-xs">Heat No (optional)</Label><Input value={currentItem.heat_no} onChange={e => setCurrentItem(p => ({...p, heat_no: e.target.value}))} className="h-9 text-sm" placeholder="HT-240318-A" /></div>
              </div>

              {/* Row 10: Customer */}
              <div>
                <Label className="text-xs">Customer (optional)</Label>
                <Select value={currentItem.customer_id || "_none_"} onValueChange={v => setCurrentItem(p => ({...p, customer_id: v === "_none_" ? "" : v}))}>
                  <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="No customer" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none_">None</SelectItem>
                    {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name} ({c.code})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2 pt-2">
                <Button onClick={handleAddItem} disabled={loading || calc.excError} className="flex-1 h-10 text-sm font-semibold" style={{ backgroundColor: '#1a2fce' }} data-testid="add-item-btn">
                  <Plus size={14} className="mr-1" /> {loading ? 'Adding...' : 'Add Item'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {pallet.items?.length > 0 && (
            <div className="flex gap-2">
              <Button onClick={handleGenerateBoxes} disabled={loading} className="flex-1 h-11 text-sm font-semibold" variant="outline" data-testid="generate-labels-btn">
                <Printer size={14} className="mr-1" /> Generate Box Labels
              </Button>
              <Button onClick={handleFinalize} disabled={loading} className="flex-1 h-11 text-sm font-semibold" style={{ backgroundColor: '#0e7a45' }} data-testid="finalize-btn">
                <Check size={14} className="mr-1" /> Finalize Pallet
              </Button>
            </div>
          )}
        </>
      )}

      {/* Step 2: Labels Generated */}
      {step === 2 && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6 text-center">
            <Printer size={40} className="mx-auto mb-3 text-[#1a2fce]" />
            <h3 className="text-lg font-semibold mb-2">Box Labels Generated</h3>
            <p className="text-sm text-gray-500 mb-4">Print the carton labels and hand them to packing workers</p>
            <div className="flex gap-2 justify-center">
              <Button onClick={handleFinalize} disabled={loading} className="h-10 text-sm font-semibold" style={{ backgroundColor: '#0e7a45' }} data-testid="finalize-after-labels">
                <Check size={14} className="mr-1" /> Finalize Pallet
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Finalized */}
      {step === 3 && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6 text-center">
            <Check size={40} className="mx-auto mb-3 text-[#0e7a45]" />
            <h3 className="text-lg font-semibold mb-2">Pallet Finalized</h3>
            <p className="text-sm text-gray-500 mb-4">{pallet.pallet_ref} is ready for placement</p>
            <Button
              onClick={async () => {
                try {
                  const res = await getAllBoxLabels(pallet.id);
                  if (res.data.url) {
                    window.open(`${API_BASE}${res.data.url}`, '_blank');
                    toast.success(`${res.data.count} box label(s) generated`);
                  } else toast.error('No box labels available');
                } catch (err) { toast.error(err.response?.data?.detail || 'Failed to generate box labels'); }
              }}
              className="w-full mb-3 h-11 text-sm font-semibold"
              style={{ backgroundColor: '#d97706' }}
              data-testid="print-box-labels-finalize"
            >
              <Printer size={14} className="mr-2" /> Print Box Labels
            </Button>
            <div className="flex gap-2 justify-center">
              <Button onClick={() => navigate(`/scan?place=${pallet.id}&ref=${pallet.pallet_ref}`)} className="h-10 text-sm font-semibold" style={{ backgroundColor: '#1a2fce' }} data-testid="scan-to-place">
                <Package size={14} className="mr-1" /> Scan Location to Place
              </Button>
              <Button onClick={() => navigate(`/pallet/${pallet.pallet_ref}`)} variant="outline" className="h-10 text-sm">
                View Pallet Details
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 4 && <div className="text-center py-8 text-gray-500">This pallet has been placed. <Button variant="link" className="text-[#1a2fce]" onClick={() => navigate(`/pallet/${pallet.pallet_ref}`)}>View Details</Button></div>}
    </div>
  );
}
